import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Skeleton,
  Space,
  Table,
  Typography,
  message,
} from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { QueryOperation } from "../../code/search-query/search-query";
import { nameOf } from "../../utils/typescript";
import {
  addCompanyCollaborators,
  addCompanyConsultants,
  deleteCompanyCollaborators,
  deleteCompanyConsultants,
  getCollaborators,
  getCompanyCollaborators,
  getCompanyConsultants,
  getConsultants,
} from "../../repository/data.repository";
import {
  Collaborator,
  Company,
  CompanyCollaborator,
  Consultant,
  MAX_PAGE_SIZE,
} from "../../repository/data.default";
import { retry, getProblemMessage } from "../../utils/http";
import ComboBox from "../../controls/ComboBox";
import { useSelector } from "../../store/store";
import { authorizeSelectors } from "../../store/authorize";
import CompanyUserPreviewModal, { CompanyUserKind } from "./CompanyUserPreviewModal";

export interface CompanyAffiliatePanelProps {
  readonly kind: "consultant" | "collaborator";
  readonly open: boolean;
  readonly companyUid: string | undefined;
  readonly companyName?: string;
  readonly reloadToken?: string | number;
  readonly onChange?: () => void;
  readonly onCountLoaded?: (count: number) => void;
  readonly scopeCompanies: Company[];
}

const KIND_CONFIG = {
  consultant: {
    titleSingular: "Consultant",
    titlePlural: "Consultants",
    actionLabel: "+ Assign Consultant",
    emptyText: "No consultants assigned yet.",
    permissionScope: "CompanyConsultants",
    listFn: getCompanyConsultants,
    pickerFn: getConsultants,
    assignFn: addCompanyConsultants,
    unassignFn: deleteCompanyConsultants,
    userKind: "consultant" as CompanyUserKind,
  },
  collaborator: {
    titleSingular: "Collaborator",
    titlePlural: "Collaborators",
    actionLabel: "+ Assign Collaborator",
    emptyText: "No collaborators assigned yet.",
    permissionScope: "CompanyCollaborators",
    listFn: getCompanyCollaborators,
    pickerFn: getCollaborators,
    assignFn: addCompanyCollaborators,
    unassignFn: deleteCompanyCollaborators,
    userKind: "collaborator" as CompanyUserKind,
  },
} as const;

function CompanyAffiliatePanel(props: CompanyAffiliatePanelProps) {
  const cfg = KIND_CONFIG[props.kind];
  const permissions = useSelector(authorizeSelectors.permissions);
  const canAssign = permissions.can(cfg.permissionScope, "create");
  const canUnassign = permissions.can(cfg.permissionScope, "delete");

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<CompanyCollaborator[]>([]);
  const [search, setSearch] = useState("");
  const [assignOpen, setAssignOpen] = useState(false);
  const [preview, setPreview] = useState<(Consultant | Collaborator) | null>(null);
  const everLoadedRef = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scopeUids = useMemo(
    () => props.scopeCompanies.map((c) => c.uid),
    [props.scopeCompanies],
  );
  const scopeKey = scopeUids.join("|");

  useEffect(() => {
    if (!props.open || scopeUids.length === 0) return;
    load(search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, scopeKey, props.reloadToken]);

  useEffect(() => {
    if (!props.open || scopeUids.length === 0) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(search), 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  function load(nameFilter: string): void {
    if (scopeUids.length === 0) return;
    setLoading(true);
    const conditions: any[] = [
      {
        field: nameOf<CompanyCollaborator>("companyUid"),
        values: scopeUids,
        operation: QueryOperation.In,
      },
    ];
    if (nameFilter.trim()) {
      conditions.push({
        field: nameOf<CompanyCollaborator>("userUserName"),
        values: [nameFilter.trim()],
        operation: QueryOperation.StartsWith,
      });
    }
    retry(() =>
      cfg.listFn({
        searchQuery: { conditions },
        pageNumber: 0,
        pageSize: MAX_PAGE_SIZE,
      }),
    )
      .then(({ data }) => {
        setItems(data);
        setLoading(false);
        everLoadedRef.current = true;
        props.onCountLoaded?.(data.length);
      })
      .catch(() => setLoading(false));
  }

  function onMutated(): void {
    setAssignOpen(false);
    load(search);
    props.onChange?.();
  }

  async function onUnassign(uid: string): Promise<void> {
    try {
      await cfg.unassignFn(uid);
      message.success(`${cfg.titleSingular} unassigned from this company.`);
      onMutated();
    } catch (err) {
      message.error(getProblemMessage(err));
    }
  }

  if (!props.open) return null;

  const showSkeleton = loading && !everLoadedRef.current;
  const multiCompany = props.scopeCompanies.length > 1;

  return (
    <Card
      size="small"
      title={
        <Space>
          <Typography.Text strong>
            {multiCompany ? `${cfg.titlePlural} (whole branch)` : cfg.titlePlural}
          </Typography.Text>
          <Typography.Text type="secondary">({items.length})</Typography.Text>
        </Space>
      }
      extra={
        <Space>
          <Input.Search
            placeholder="Search by user name…"
            allowClear
            size="small"
            style={{ width: 200 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button
            type="primary"
            size="small"
            disabled={!canAssign || !props.companyUid}
            onClick={() => setAssignOpen(true)}
          >
            {cfg.actionLabel}
          </Button>
        </Space>
      }
    >
      {showSkeleton ? (
        <Skeleton active paragraph={{ rows: 3 }} title={false} />
      ) : (
        <Table
          size="small"
          loading={loading}
          rowKey={(r) => r.uid}
          dataSource={items}
          pagination={items.length > 10 ? { pageSize: 10 } : false}
          locale={{ emptyText: cfg.emptyText }}
          columns={[
            { title: "User Name", dataIndex: "userUserName", key: "userUserName" },
            ...(multiCompany
              ? [
                  {
                    title: "Assigned To (Company)",
                    dataIndex: "companyName",
                    key: "companyName",
                  },
                ]
              : []),
            {
              title: "Assigned On",
              dataIndex: "createdOn",
              key: "createdOn",
              render: (v: string | undefined) =>
                v ? new Date(v).toLocaleDateString("en-GB") : "—",
            },
            {
              title: "Assigned By",
              dataIndex: "createdByUserUserName",
              key: "createdByUserUserName",
            },
            {
              title: "",
              key: "actions",
              width: 110,
              render: (_: any, record: CompanyCollaborator) => (
                <Popconfirm
                  title={`Unassign this ${cfg.titleSingular.toLowerCase()}?`}
                  description="The affiliate stays on the platform; only the link to this company is removed."
                  okText="Unassign"
                  okButtonProps={{ danger: true }}
                  disabled={!canUnassign}
                  onConfirm={() => onUnassign(record.uid)}
                >
                  <Button
                    size="small"
                    type="link"
                    danger
                    disabled={!canUnassign}
                  >
                    Unassign
                  </Button>
                </Popconfirm>
              ),
            },
          ]}
        />
      )}
      <CompanyUserPreviewModal
        open={!!preview}
        kind={cfg.userKind}
        user={preview}
        onClose={() => setPreview(null)}
      />
      <AssignAffiliateDialog
        open={assignOpen}
        kind={props.kind}
        scopeCompanies={props.scopeCompanies}
        defaultCompanyUid={props.companyUid}
        onCancel={() => setAssignOpen(false)}
        onFinish={onMutated}
      />
    </Card>
  );
}

function AssignAffiliateDialog(props: {
  open: boolean;
  kind: "consultant" | "collaborator";
  scopeCompanies: Company[];
  defaultCompanyUid: string | undefined;
  onCancel: () => void;
  onFinish: () => void;
}) {
  const cfg = KIND_CONFIG[props.kind];
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (!props.open) {
      form.resetFields();
      setError("");
    } else {
      const def = props.scopeCompanies.find((c) => c.uid === props.defaultCompanyUid);
      if (def) form.setFieldsValue({ company: { display: def.name, value: def.uid } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  const companyRequest = (search: string) => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? props.scopeCompanies.filter((c) => c.name.toLowerCase().startsWith(q))
      : props.scopeCompanies;
    return Promise.resolve(
      filtered.map((c) => ({ display: c.name, value: c.uid, dataItem: c })),
    );
  };

  const pickerRequest = (search: string) =>
    retry(() =>
      cfg.pickerFn({
        searchQuery: {
          conditions: search
            ? [
                {
                  field: nameOf<Consultant>("firstName"),
                  values: [search],
                  operation: QueryOperation.StartsWith,
                },
              ]
            : [],
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) =>
      data.map((d: Consultant | Collaborator) => ({
        display: `${d.firstName} ${d.lastName} (${d.userName})`,
        value: d.uid,
        dataItem: d,
      })),
    );

  async function onSubmit(): Promise<void> {
    try {
      const values = await form.validateFields();
      setSaving(true);
      setError("");
      await cfg.assignFn({
        companyUid: values.company?.value,
        userUid: values.user?.value,
      });
      message.success(
        `${cfg.titleSingular} assigned to "${values.company?.display}".`,
      );
      setSaving(false);
      props.onFinish();
    } catch (err: any) {
      setSaving(false);
      if (err?.errorFields) return;
      setError(getProblemMessage(err));
    }
  }

  return (
    <Modal
      open={props.open}
      title={`Assign ${cfg.titleSingular}`}
      onCancel={props.onCancel}
      onOk={onSubmit}
      okText="Assign"
      confirmLoading={saving}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="company"
          label="Assign to (company)"
          rules={[{ required: true, message: "Company is required" }]}
        >
          <ComboBox request={companyRequest} />
        </Form.Item>
        <Form.Item
          name="user"
          label={`Pick a ${cfg.titleSingular.toLowerCase()} from the platform roster`}
          rules={[{ required: true, message: `${cfg.titleSingular} is required` }]}
        >
          <ComboBox request={pickerRequest} />
        </Form.Item>
      </Form>
      <Typography.Text type="secondary" className="block">
        Only existing platform {cfg.titlePlural.toLowerCase()} are listed. To register a new one, use the
        Affiliates page.
      </Typography.Text>
      {error && (
        <Typography.Text type="danger" className="mt-2 block">
          {error}
        </Typography.Text>
      )}
    </Modal>
  );
}

export default CompanyAffiliatePanel;
